import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { QUERY_GC_TIME_MS, QUERY_STALE_TIME_MS } from '@/config/constants'
import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth, isRecord, requireAuthToken } from '@/lib/api'
import { clientDutyKeys } from '@/lib/queryKeys'

/** One duty the client is assigned to (relay's PatientDutyRow). */
export interface ClientDuty {
  patient_duty_id: string
  duty_id: string
  duty_name: string
  /** The duty itself: 'active' | 'inactive' | 'archived'. */
  duty_lifecycle_status: string
  /** The client's status in the duty, e.g. 'pending', 'processing', 'completed'. */
  assignment_status: string | null
  /** Relay's verdict: the assignment status is one duty progress counts as done. */
  is_done: boolean
  duty_attempt_number: number | null
  /** Waiting for its next call attempt. */
  retry_due: boolean
  assigned_at: string
  updated_at: string
}

function mapClientDuty(row: unknown): ClientDuty {
  if (!isRecord(row) || typeof row.duty_id !== 'string') {
    throw new Error('Invalid client duty row')
  }
  return {
    patient_duty_id: String(row.patient_duty_id),
    duty_id: row.duty_id,
    duty_name: String(row.duty_name),
    duty_lifecycle_status: String(row.duty_lifecycle_status),
    assignment_status:
      typeof row.assignment_status === 'string' ? row.assignment_status : null,
    is_done: row.is_done === true,
    duty_attempt_number:
      typeof row.duty_attempt_number === 'number'
        ? row.duty_attempt_number
        : null,
    retry_due: row.retry_due === true,
    assigned_at: String(row.assigned_at),
    updated_at: String(row.updated_at),
  }
}

/** Every duty one client is assigned to, newest assignment first. */
export function useClientDutiesQuery(
  patientId: string | undefined,
  options: { enabled?: boolean } = {}
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: clientDutyKeys.list(organization?.id, patientId),
    enabled: !!organization?.id && !!patientId && (options.enabled ?? true),
    queryFn: async ({ signal }) => {
      const token = await requireAuthToken(getToken)
      const response = await fetchJsonWithAuth<unknown>({
        token,
        url: `${AGENT_URL}/data/patients/${encodeURIComponent(patientId ?? '')}/duties`,
        signal,
        errorMessage: 'Failed to fetch client duties',
      })
      if (!isRecord(response) || !Array.isArray(response.data)) {
        throw new Error('Invalid client duties response')
      }
      return response.data.map(mapClientDuty)
    },
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}
